/**
 * Leviathan - Maintenance Form Page
 * Quasar Prime LLC
 *
 * Create and edit maintenance records
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { parseApiError } from '@/utils/errorHandler';
import {
  Loader2,
  ArrowLeft,
  Save,
} from 'lucide-react';

import { API_URL } from '@/config/api';

interface TruckOption {
  id: string;
  unitNumber: string;
  make: string;
  model: string;
  currentMileage: number | null;
}

const MAINTENANCE_TYPES = [
  { value: 'OIL_CHANGE', label: 'Oil Change' },
  { value: 'TIRE_SERVICE', label: 'Tire Service' },
  { value: 'BRAKE_SERVICE', label: 'Brake Service' },
  { value: 'DOT_INSPECTION', label: 'DOT Inspection' },
  { value: 'PUMP_CALIBRATION', label: 'Pump Calibration' },
  { value: 'METER_CALIBRATION', label: 'Meter Calibration' },
  { value: 'GENERAL_REPAIR', label: 'General Repair' },
  { value: 'PREVENTIVE', label: 'Preventive Maintenance' },
  { value: 'OTHER', label: 'Other' },
];

export default function MaintenanceForm() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    truckId: searchParams.get('truckId') || '',
    truckNumber: '',
    truckVin: '',
    maintenanceType: 'OIL_CHANGE',
    description: '',
    scheduledDate: new Date().toISOString().split('T')[0],
    mileageAtService: '',
    nextServiceMileage: '',
    nextServiceDate: '',
    laborCost: '',
    partsCost: '',
    vendorName: '',
    invoiceNumber: '',
    notes: '',
    performedBy: '',
  });

  const [error, setError] = useState<string | null>(null);

  // Fetch trucks
  const { data: trucksData } = useQuery({
    queryKey: ['trucks-list'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/trucks?isActive=true&limit=100`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch trucks');
      return res.json();
    },
  });

  // Fetch existing record if editing
  const { data: recordData, isLoading: recordLoading } = useQuery({
    queryKey: ['maintenance-record', id],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/maintenance/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch record');
      return res.json();
    },
    enabled: isEditing,
  });

  const trucks: TruckOption[] = trucksData?.items || [];

  // Update truck info when truck changes
  useEffect(() => {
    if (formData.truckId && trucks.length > 0) {
      const truck = trucks.find((t) => t.id === formData.truckId);
      if (truck) {
        setFormData((prev) => ({
          ...prev,
          truckNumber: truck.unitNumber,
          mileageAtService: prev.mileageAtService || truck.currentMileage?.toString() || '',
        }));
      }
    }
  }, [formData.truckId, trucks]);

  // Populate form when editing
  useEffect(() => {
    if (recordData?.record) {
      const r = recordData.record;
      setFormData({
        truckId: r.truckId,
        truckNumber: r.truckNumber,
        truckVin: r.truckVin || '',
        maintenanceType: r.maintenanceType,
        description: r.description,
        scheduledDate: r.scheduledDate ? r.scheduledDate.split('T')[0] : '',
        mileageAtService: r.mileageAtService?.toString() || '',
        nextServiceMileage: r.nextServiceMileage?.toString() || '',
        nextServiceDate: r.nextServiceDate ? r.nextServiceDate.split('T')[0] : '',
        laborCost: r.laborCost?.toString() || '',
        partsCost: r.partsCost?.toString() || '',
        vendorName: r.vendorName || '',
        invoiceNumber: r.invoiceNumber || '',
        notes: r.notes || '',
        performedBy: r.performedBy || '',
      });
    }
  }, [recordData]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const payload = {
        truckId: data.truckId,
        truckNumber: data.truckNumber,
        truckVin: data.truckVin || undefined,
        maintenanceType: data.maintenanceType,
        description: data.description,
        scheduledDate: data.scheduledDate,
        mileageAtService: data.mileageAtService ? parseInt(data.mileageAtService) : undefined,
        nextServiceMileage: data.nextServiceMileage
          ? parseInt(data.nextServiceMileage)
          : undefined,
        nextServiceDate: data.nextServiceDate || undefined,
        laborCost: data.laborCost ? parseFloat(data.laborCost) : undefined,
        partsCost: data.partsCost ? parseFloat(data.partsCost) : undefined,
        vendorName: data.vendorName || undefined,
        invoiceNumber: data.invoiceNumber || undefined,
        notes: data.notes || undefined,
        performedBy: data.performedBy || undefined,
      };

      const url = isEditing ? `${API_URL}/api/maintenance/${id}` : `${API_URL}/api/maintenance`;

      const res = await fetch(url, {
        method: isEditing ? 'PATCH' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorMessage = await parseApiError(res);
        throw new Error(errorMessage);
      }
      return res.json();
    },
    onSuccess: (data) => {
      navigate(`/fleet/maintenance/${data.record.id}`);
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.truckId || !formData.description) {
      setError('Truck and Description are required');
      return;
    }

    saveMutation.mutate(formData);
  };

  const totalCost =
    (parseFloat(formData.laborCost) || 0) + (parseFloat(formData.partsCost) || 0);

  if (isEditing && recordLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-4 mb-2">
            <Link
              to={isEditing ? `/fleet/maintenance/${id}` : '/fleet/maintenance'}
              className="text-slate-400 hover:text-amber-400 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-2xl font-bold text-slate-100">
              {isEditing ? 'Edit Maintenance' : 'Schedule Maintenance'}
            </h1>
          </div>
          <p className="text-slate-400 text-sm ml-9">
            {isEditing ? 'Update maintenance record' : 'Schedule service for a truck'}
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-4 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Truck & Type */}
        <Panel title="Truck & Service Type">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-slate-400 text-sm mb-2">Truck *</label>
              <select
                value={formData.truckId}
                onChange={(e) => setFormData((prev) => ({ ...prev, truckId: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                required
              >
                <option value="">Select truck...</option>
                {trucks.map((truck) => (
                  <option key={truck.id} value={truck.id}>
                    {truck.unitNumber} - {truck.make} {truck.model}
                  </option>
                ))}
              </select>
              {formData.truckId && trucks.find((t) => t.id === formData.truckId)?.currentMileage && (
                <p className="text-slate-500 text-xs mt-1">
                  Current mileage:{' '}
                  {trucks
                    .find((t) => t.id === formData.truckId)
                    ?.currentMileage?.toLocaleString()}{' '}
                  mi
                </p>
              )}
            </div>

            <div>
              <label className="block text-slate-400 text-sm mb-2">Service Type *</label>
              <select
                value={formData.maintenanceType}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, maintenanceType: e.target.value }))
                }
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                required
              >
                {MAINTENANCE_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Panel>

        {/* Service Details */}
        <Panel title="Service Details">
          <div className="space-y-6">
            <div>
              <label className="block text-slate-400 text-sm mb-2">Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, description: e.target.value }))
                }
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50 resize-none"
                rows={3}
                placeholder="Describe the service to be performed..."
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-slate-400 text-sm mb-2">Scheduled Date</label>
                <input
                  type="date"
                  value={formData.scheduledDate}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, scheduledDate: e.target.value }))
                  }
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                />
              </div>

              <div>
                <label className="block text-slate-400 text-sm mb-2">Mileage at Service</label>
                <input
                  type="number"
                  value={formData.mileageAtService}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, mileageAtService: e.target.value }))
                  }
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                  placeholder="Current mileage"
                />
              </div>
            </div>
          </div>
        </Panel>

        {/* Costs */}
        <Panel title="Costs">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-slate-400 text-sm mb-2">Labor Cost</label>
              <input
                type="number"
                value={formData.laborCost}
                onChange={(e) => setFormData((prev) => ({ ...prev, laborCost: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                step="0.01"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-slate-400 text-sm mb-2">Parts Cost</label>
              <input
                type="number"
                value={formData.partsCost}
                onChange={(e) => setFormData((prev) => ({ ...prev, partsCost: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                step="0.01"
                placeholder="0.00"
              />
            </div>

            <div className="flex items-end">
              <div className="text-center p-4 bg-amber-500/10 border border-amber-500/50 rounded-lg flex-1">
                <div className="text-amber-400 text-xs uppercase tracking-wider mb-1">Total</div>
                <div className="text-amber-400 text-xl font-bold">${totalCost.toFixed(2)}</div>
              </div>
            </div>
          </div>
        </Panel>

        {/* Vendor */}
        <Panel title="Vendor Information">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-slate-400 text-sm mb-2">Vendor Name</label>
              <input
                type="text"
                value={formData.vendorName}
                onChange={(e) => setFormData((prev) => ({ ...prev, vendorName: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                placeholder="Shop or service center name"
              />
            </div>

            <div>
              <label className="block text-slate-400 text-sm mb-2">Invoice Number</label>
              <input
                type="text"
                value={formData.invoiceNumber}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, invoiceNumber: e.target.value }))
                }
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
          </div>
        </Panel>

        {/* Next Service */}
        <Panel title="Next Service Due">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-slate-400 text-sm mb-2">At Mileage</label>
              <input
                type="number"
                value={formData.nextServiceMileage}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, nextServiceMileage: e.target.value }))
                }
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                placeholder="Schedule next service at this mileage"
              />
            </div>

            <div>
              <label className="block text-slate-400 text-sm mb-2">By Date</label>
              <input
                type="date"
                value={formData.nextServiceDate}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, nextServiceDate: e.target.value }))
                }
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
          </div>
        </Panel>

        {/* Notes */}
        <Panel title="Notes">
          <div className="space-y-6">
            <div>
              <label className="block text-slate-400 text-sm mb-2">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50 resize-none"
                rows={3}
                placeholder="Additional notes or observations..."
              />
            </div>

            <div>
              <label className="block text-slate-400 text-sm mb-2">Performed By</label>
              <input
                type="text"
                value={formData.performedBy}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, performedBy: e.target.value }))
                }
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                placeholder="Technician name"
              />
            </div>
          </div>
        </Panel>

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <Link
            to={isEditing ? `/fleet/maintenance/${id}` : '/fleet/maintenance'}
            className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-slate-100 rounded-lg transition-colors"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={saveMutation.isPending}
            className="flex items-center gap-2 px-6 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {saveMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {isEditing ? 'Update Record' : 'Schedule Service'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
      <h3 className="text-lg font-semibold text-slate-100 mb-6 pb-3 border-b border-slate-700">
        {title}
      </h3>
      {children}
    </div>
  );
}
